import styled from '@emotion/styled/macro';
import {
  Autocomplete,
  Button,
  CircularProgress,
  FormLabel,
  TextField,
} from '@mui/material';
import api from '@tyrio/api-factory';
import { DBBranchesApi } from '@tyrio/dto';
import {
  ToastHelper,
  ToastMessageType,
  ToastType,
  TyrioSelectInputOption,
} from '@tyrio/ui-library';
import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';

const StockListUpdate = () => {
  const [branches, setBranches] = useState<TyrioSelectInputOption[]>([]);
  const [selectedBranch, setSelectedBranch] =
    useState<TyrioSelectInputOption | null>(null);

  useQuery(
    ['get_branches'],
    async () => {
      return await api.fetch<DBBranchesApi['list']>('get_branches', {
        search: '',
      });
    },
    {
      onSuccess: (data) => {
        const dropdownItems: TyrioSelectInputOption[] = [];
        data.data.forEach((item) =>
          dropdownItems.push({
            label: item.branchName,
            value: item.id.toString(),
          })
        );

        setBranches(dropdownItems);
      },
    }
  );

  const updateStockList = useMutation(
    () =>
      api.fetch<{
        requestBody: never;
        request: { branchId: string };
        response: never;
        requestParams: { branchId: string };
      }>('adjust_stock_list', {
        branchId: selectedBranch?.value.toString() ?? '',
      }),
    {
      mutationKey: 'adjust_stock_list',
      onSuccess: () => {
        setSelectedBranch(null);
        ToastHelper.showToast(
          'Stock list update',
          ToastType.SUCCESS,
          ToastMessageType.UPDATE,
          `Stock list for branch ${selectedBranch?.label} updated successfully`
        );
      },
      onError: () => {
        ToastHelper.showToast(
          'Stock list update',
          ToastType.ERROR,
          ToastMessageType.ERROR
        );
      },
    }
  );

  return (
    <Wrapper>
      <div style={{ marginBottom: '10px' }}>
        <FormLabel>Update stock list:</FormLabel>
      </div>

      <FieldsWrapper>
        <Autocomplete
          disablePortal
          id="branchId"
          getOptionLabel={(option) => {
            if (typeof option === 'string')
              return branches.find((el) => el.value === option)?.label || '';
            else
              return (
                branches.find((el) => el.value === option.value)?.label || ''
              );
          }}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onChange={(_event, newValue) => {
            setSelectedBranch(newValue);
          }}
          options={branches}
          sx={{ width: '100%' }}
          renderInput={(params) => <TextField {...params} label="Branch" />}
        />

        <Button
          variant="contained"
          color="info"
          sx={{ width: '10%', height: '50px' }}
          disabled={!selectedBranch || updateStockList.isLoading}
          startIcon={
            updateStockList.isLoading ? <CircularProgress size={10} /> : null
          }
          onClick={() => updateStockList.mutate()}
        >
          Update
        </Button>
      </FieldsWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  width: 100%;
`;

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  width: 100%;
  align-items: center;
`;

export default StockListUpdate;
